import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@onward-delivery/design';
import { ArrowLeft } from '@onward-delivery/design/icons';
import { useDrillInReturn, useTabParam } from './hooks';

export { useDrillIn, useDrillInReturn, useTabParam, tabValueFor } from './hooks';

/**
 * @typedef {Object} DrillInTab
 * @property {string} value the tab's `?tab=` value
 * @property {string} label
 * @property {React.ReactNode} content
 */

/**
 * The page for one item opened from a list or card: a back button to where it was opened from, the page's header,
 * and its tabs. The active tab is in the query string. The page brings its own look: `Tabs` renders the tab row and
 * the active tab's content, and `Surface` is the page around it all.
 *
 * Open one with `useDrillIn()` so the back button returns to the same view and tab.
 *
 * @param {{
 *   backLabel: string,
 *   fallbackPath: string,
 *   header: React.ReactNode,
 *   tabs: DrillInTab[],
 *   Tabs: React.ComponentType<{ tabs: DrillInTab[], value: string, onChange: (value: string) => void }>,
 *   tabParam?: string,
 *   Surface?: React.ElementType,
 * }} props `fallbackPath` is where back goes when the page wasn't opened with `useDrillIn`; `tabParam` is the query
 *   param holding the tab
 */
const DrillInPage = ({ backLabel, fallbackPath, header, tabs, Tabs, tabParam = 'tab', Surface = 'div' }) => {
    const navigate = useNavigate();
    const backTo = useDrillInReturn(fallbackPath);
    const [activeTab, setActiveTab] = useTabParam(tabs, tabParam);

    return (
        <Surface>
            <div className="onward-design">
                <Button variant="ghost" size="sm" onClick={() => navigate(backTo)} className="-ml-3">
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    {backLabel}
                </Button>
            </div>
            {header}
            <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
        </Surface>
    );
};

export default DrillInPage;
